/** Client-safe retirement rules and display contract. No server imports. */
export interface RetirementPerson {
  id: string;
  targetFireYear: number | null;
  actualRetirementYear?: number | null;
}
export type RetirementChoice =
  | 'keep_working'
  | 'plan_retirement'
  | 'already_retired';
export type PlanHealth =
  | 'on_track'
  | 'watch'
  | 'review_needed'
  | 'needs_review';
export type AssessmentBasis = 'retire_now' | 'selected_schedule';
export interface FireAssessment {
  calculatedAt: string;
  projectionYear: number;
  inputRevision: number;
  projectionRevision?: number;
  modelVersion: string;
  retireNowRate: number | null;
  selectedRate: number | null;
  threshold: number;
  endYear: number;
  estateTarget: number;
  projectedFiYear: number | null;
  basis: AssessmentBasis;
  scheduleKey?: string;
  qualityReasons: string[];
}
export interface FiMilestone {
  id: string;
  achievedAt: string;
  threshold: number;
  successRate: number;
}
export interface HealthHistory {
  status: PlanHealth;
  belowSince: string | null;
  recoverySince: string | null;
  basisKey: string;
  lastReviewRate?: number | null;
}
export interface FireLifecycleView {
  enabled: boolean;
  canDecide: boolean;
  revision: number;
  assessment: FireAssessment | null;
  milestone: FiMilestone | null;
  health: PlanHealth;
  decision: RetirementChoice | null;
  pendingCelebration: boolean;
  persons: Array<
    RetirementPerson & { name: string; type: string; birthYear: number | null }
  >;
}
export function retirementYear(person: RetirementPerson): number | null {
  return person.actualRetirementYear ?? person.targetFireYear;
}
export function resolveRetirementYears(
  people: RetirementPerson[],
  now: number,
  retireNow = false,
): Map<string, number> {
  return new Map(
    people.flatMap((p) => {
      const year =
        p.actualRetirementYear ?? (retireNow ? now : p.targetFireYear);
      return year == null ? [] : [[p.id, year] as [string, number]];
    }),
  );
}
export function lifecycleBasis(people: RetirementPerson[]): AssessmentBasis {
  const workers = people.filter((p) => p.actualRetirementYear == null);
  return workers.length === 0 || workers.some((p) => p.targetFireYear != null)
    ? 'selected_schedule'
    : 'retire_now';
}
export function displayedSuccessRate(a: FireAssessment): number | null {
  return a.basis === 'retire_now' ? a.retireNowRate : a.selectedRate;
}
export function isValidRate(rate: number | null): rate is number {
  return rate != null && Number.isFinite(rate) && rate >= 0 && rate <= 1;
}
export function evaluateHealth(
  a: FireAssessment,
  previous: HealthHistory | null,
): HealthHistory {
  const key = `${a.basis}:${a.endYear}:${a.threshold}:${a.estateTarget}:${a.modelVersion}:${a.scheduleKey ?? ''}`;
  const prev = previous?.basisKey === key ? previous : null;
  const rate = displayedSuccessRate(a);
  const now = Date.parse(a.calculatedAt);
  if (a.qualityReasons.length || !isValidRate(rate)) {
    return {
      status: 'needs_review',
      belowSince: prev?.belowSince ?? null,
      recoverySince: null,
      basisKey: key,
      lastReviewRate: prev?.lastReviewRate ?? null,
    };
  }
  if (rate < a.threshold) {
    const belowSince = prev?.belowSince ?? a.calculatedAt;
    return {
      status:
        a.threshold - rate >= 0.05 - 1e-10 ||
        now - Date.parse(belowSince) >= 86400000
          ? 'review_needed'
          : 'watch',
      belowSince,
      recoverySince: null,
      basisKey: key,
      lastReviewRate: prev?.lastReviewRate ?? null,
    };
  }
  return {
    status: 'on_track',
    belowSince: null,
    recoverySince:
      prev?.status === 'watch' || prev?.status === 'review_needed'
        ? a.calculatedAt
        : (prev?.recoverySince ?? null),
    basisKey: key,
    lastReviewRate: prev?.lastReviewRate ?? null,
  };
}
export function qualifiesForMilestone(a: FireAssessment): boolean {
  return (
    a.qualityReasons.length === 0 &&
    isValidRate(a.retireNowRate) &&
    a.retireNowRate >= a.threshold
  );
}
export function usesRetirementConfidence(v: Pick<FireLifecycleView, 'milestone' | 'persons'>): boolean {
  return !!v.milestone || v.persons.some(
    (p) => p.actualRetirementYear != null || p.targetFireYear != null,
  );
}
export function lifecycleTitle(v: FireLifecycleView): string | null {
  const a = v.assessment;
  if (!a || !usesRetirementConfidence(v)) return null;
  const rate = displayedSuccessRate(a);
  return `${isValidRate(rate) ? `${Math.round(rate * 100)}%` : 'Unavailable'} modeled success`;
}

export function employmentAppliesInYear(args: {
  stream: {
    personId?: string | null;
    continuesInRetirement?: boolean;
    begin?: string | null;
    end?: string | null;
  };
  year: number;
  fireYear: number | null;
  retirementYears: ReadonlyMap<string, number>;
  fallbackPersonId?: string | undefined;
}): boolean {
  const { stream, year } = args;
  if (stream.begin && year < Number(stream.begin.slice(0, 4))) return false;
  if (stream.end && year >= Number(stream.end.slice(0, 4))) return false;
  if (stream.continuesInRetirement) return true;
  const owner = stream.personId ?? args.fallbackPersonId;
  const stop = owner ? args.retirementYears.get(owner) : undefined;
  return stop != null
    ? year < stop
    : args.fireYear == null || year < args.fireYear;
}
